import React, { useState, useEffect } from 'react';
import { ExternalLink, ArrowRight, Loader } from 'lucide-react';
import { BACKEND_URL, DISCORD_CLIENT_ID } from '../config';

const JoinDiscordStep1 = ({ discordLink, token }) => {
  const [hasJoined, setHasJoined] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const savedToken = sessionStorage.getItem('onboarding_token');
    const tokenParam = urlParams.get('token') || savedToken || token;

    if (code && tokenParam) {
      handleOAuthCallback(code, tokenParam);
    }
  }, [token]);

  const handleOAuthCallback = async (code, tokenParam) => {
    setIsProcessing(true);
    setError('');

    window.history.replaceState({}, document.title, `/join-discord?token=${tokenParam}`);

    try {
      const response = await fetch(`${BACKEND_URL}/api/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'mark-discord-step-2',
          discordCode: code,
          token: tokenParam,
        }),
      });

      const data = await response.json();

      if (data.success) {
        window.location.href = `/join-discord?token=${tokenParam}`;
      } else {
        console.error('OAuth failed:', data);
        setError(data.error || data.details || 'Error al procesar OAuth');
      }
    } catch (err) {
      console.error('OAuth exception:', err);
      setError('Error al procesar. Intenta de nuevo.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenDiscord = async () => {
    setIsProcessing(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'mark-discord-step-1',
          token: token,
        }),
      });

      const data = await response.json();

      if (data.success) {
        window.open(discordLink, '_blank', 'noopener,noreferrer');
        setHasJoined(true);
      } else {
        setError(data.error || 'Error al actualizar');
      }
    } catch {
      setError('Error al procesar. Intenta de nuevo.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleContinue = () => {
    sessionStorage.setItem('onboarding_token', token);

    const host = window.location.host?.toLowerCase() || '';
    const isStage = host.includes('verifypage');

    const redirectUri = isStage
      ? 'https://verifypageonboarding.nosoyliquidez.com/join-discord'
      : 'https://onboarding.nosoyliquidez.com/join-discord';

    const encodedRedirectUri = encodeURIComponent(redirectUri);
    const scope = 'identify guilds.members.read';

    const discordAuthUrl = `https://discord.com/api/oauth2/authorize?client_id=${DISCORD_CLIENT_ID}&redirect_uri=${encodedRedirectUri}&response_type=code&scope=${encodeURIComponent(scope)}`;

    window.location.href = discordAuthUrl;
  };

  if (isProcessing) {
    return (
      <div className="text-center py-8">
        <Loader className="w-16 h-16 text-yellow-500 animate-spin mx-auto mb-6" />
        <h2 className="text-2xl font-bold text-white mb-4">Procesando...</h2>
        <p className="text-gray-400">Estamos verificando tu cuenta de Discord.</p>
      </div>
    );
  }

  return (
    <div className="text-center py-8">
      <div className="text-7xl mb-6 animate-bounce">💬</div>
      <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
        ¡Únete a Nuestra Comunidad!
      </h2>
      <p className="text-gray-300 mb-8 text-lg">
        Para completar tu onboarding, necesitas estar en el servidor de Discord de NSL.
      </p>

      <div className="max-w-md mx-auto mb-8 p-6 bg-[#5865F2]/10 border border-[#5865F2]/30 rounded-xl">
        <div className="flex items-center justify-center gap-3 mb-4">
          <svg className="w-8 h-8 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
          </svg>
          <h3 className="text-xl font-bold text-white">Comunidad Discord NSL</h3>
        </div>
        <p className="text-gray-300 text-sm mb-4">
          Felicitaciones por haber aprobado el examen de ingreso a la única comunidad de Discord enfocada 100% a conceptos de ICT SMC.
        </p>
        
        {!hasJoined ? (
          <div className="text-left bg-gray-900/50 rounded-lg p-4">
            <p className="text-yellow-500 font-semibold text-sm mb-2">📋 Paso 1 de 3</p>
            <p className="text-gray-300 text-sm">
              Haz clic en el botón de abajo para abrir Discord. Si ya eres miembro, perfecto. Si no, acepta la invitación para unirte.
            </p>
          </div>
        ) : (
          <div className="text-left bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <p className="text-green-500 font-semibold text-sm mb-2">✅ Discord abierto</p>
            <p className="text-gray-300 text-sm">
              Una vez que te hayas unido al servidor (o si ya eras miembro), regresa aquí y haz clic en "Ya me uní, Continuar".
            </p>
          </div>
        )}
      </div>

      {!hasJoined ? (
        <button
          onClick={handleOpenDiscord}
          disabled={isProcessing}
          className="w-full max-w-md mx-auto bg-[#5865F2] hover:bg-[#4752C4] text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-[#5865F2]/50 flex items-center justify-center gap-3"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
          </svg>
          Acceder
          <ExternalLink className="w-5 h-5" />
        </button>
      ) : (
        <button
          onClick={handleContinue}
          disabled={isProcessing}
          className="w-full max-w-md mx-auto bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-yellow-500/50 flex items-center justify-center gap-3"
        >
          Ya me uní, Continuar
          <ArrowRight className="w-5 h-5" />
        </button>
      )}

      {error && (
        <div className="max-w-md mx-auto mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-500 text-sm">{error}</p>
        </div>
      )}

      <p className="text-xs text-gray-500 mt-6 max-w-md mx-auto">
        {!hasJoined 
          ? 'Se abrirá Discord en una nueva pestaña. Acepta la invitación si no eres miembro aún.'
          : 'Se te pedirá autorizar a NSL para verificar tu identidad de Discord.'
        }
      </p>
    </div>
  );
};

export default JoinDiscordStep1;